import React, { useState, useContext, useEffect } from 'react';
import {
  Select,
  MenuItem,
  SelectChangeEvent,
  Typography,
  Box,
  FormControlLabel,
  Checkbox,
  FormHelperText,
} from '@mui/material';
import { useAtom, useAtomValue } from 'jotai';
import { validationDismissalDialogAtom, filteredExtractionIdsAtom } from '../../atoms/spreadsheet-atoms';
import { ValidationDismissalReason } from '../../types/validation-types';
import useGlynt from '../hooks/useGlynt';
import { AppContext } from '../../App';
import { useQueryClient } from '@tanstack/react-query';
import DialogGlynt from '../common/Dialog/DialogGlynt';
import DialogTitleGlynt from '../common/Dialog/DialogTitleGlynt';
import DialogContentGlynt from '../common/Dialog/DialogContentGlynt';
import DialogActionsGlynt from '../common/Dialog/DialogActionsGlynt';
import { FormControlStyled, FormLabelStyled } from '../common/Dialog/DialogGlynt';
import { MarkStateContext } from '../../context/MarkContext';

const ValidationDismissalDialog: React.FC = () => {
  const [dialogState, setDialogState] = useAtom(validationDismissalDialogAtom);
  const filteredExtractionIds = useAtomValue(filteredExtractionIdsAtom);
  const [selectedReason, setSelectedReason] = useState<string | null>(
    dialogState.currentDismissalReason
  );
  const [applyToAll, setApplyToAll] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showValidationError, setShowValidationError] = useState(false);
  const { setToast } = useContext(AppContext);
  const { validationSummaryByExtractionId } = useContext(MarkStateContext);
  const glynt = useGlynt();
  const queryClient = useQueryClient();

  // Pre-populate the select when dialog opens with existing dismissal reason
  useEffect(() => {
    setSelectedReason(dialogState.currentDismissalReason);
    setApplyToAll(false);
    setShowValidationError(false);
  }, [dialogState.currentDismissalReason, dialogState.isOpen]);

  const handleClose = () => {
    setDialogState({
      isOpen: false,
      dataPoolId: null,
      validationId: null,
      extractionId: null,
      field: null,
      currentDismissalReason: null,
      error: null,
      errorDetail: null,
    });
    setSelectedReason(null);
    setApplyToAll(false);
  };

  const handleReasonChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setSelectedReason(value === '' ? null : value);
    // Clear validation error when user selects something
    if (value) {
      setShowValidationError(false);
    }
  };

  const handleSubmit = async () => {
    if (!dialogState.dataPoolId || dialogState.validationId == null) {
      return;
    }

    // Require dismissal reason
    if (!dialogState.currentDismissalReason && !selectedReason) {
      setShowValidationError(true);
      return;
    }

    setIsSubmitting(true);

    try {
      const glyntInstance = await glynt;

      if (applyToAll && validationSummaryByExtractionId && dialogState.field) {
        // Filter to only include extractions that are currently visible in the grid, or use all if no filters applied
        const filteredValidations =
          filteredExtractionIds.length > 0
            ? validationSummaryByExtractionId.filter((extractionValidation) =>
                filteredExtractionIds.includes(extractionValidation.extractionId)
              )
            : validationSummaryByExtractionId;

        // Collect all validation IDs for this field across filtered extractions
        const validationIds: number[] = [];
        filteredValidations.forEach((extractionValidation) => {
          const fieldError = extractionValidation.validationErrorDetails?.find(
            (error) => error.field === dialogState.field
          );
          if (fieldError) {
            validationIds.push(fieldError.id);
          }
        });

        // Update all validation dismissals
        await Promise.all(
          validationIds.map((validationId) =>
            glyntInstance.dataPool.updateValidationDismissal(
              dialogState.dataPoolId!,
              validationId,
              selectedReason
            )
          )
        );

        // Invalidate all extraction validation queries for filtered extractions
        const extractionIds = filteredValidations.map((v) => v.extractionId);
        extractionIds.forEach((extractionId) => {
          queryClient.invalidateQueries({
            queryKey: ['extraction-validation', extractionId]
          });
        });
      } else {
        // Single validation dismissal
        await glyntInstance.dataPool.updateValidationDismissal(
          dialogState.dataPoolId,
          dialogState.validationId,
          selectedReason
        );

        // Invalidate validation queries to refresh the data
        if (dialogState.extractionId) {
          queryClient.invalidateQueries({
            queryKey: ['extraction-validation', dialogState.extractionId]
          });
        }
      }

      setToast({
        variant: 'success',
        open: true,
        message: selectedReason
          ? applyToAll
            ? 'Validation errors dismissed for all extractions'
            : 'Validation error dismissed'
          : applyToAll
            ? 'Validation error dismissals removed for all extractions'
            : 'Validation error dismissal removed',
      });

      handleClose();
    } catch (error) {
      console.error('Failed to update validation dismissal:', error);
      setToast({
        variant: 'error',
        open: true,
        message: 'Failed to update validation dismissal',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DialogGlynt
      open={dialogState.isOpen}
      closeModal={handleClose}
      cypressId="validation-dismissal-dialog"
      width="500px"
    >
      <DialogTitleGlynt title="Dismiss Validation Error" closeModal={handleClose} />
      <DialogContentGlynt>
        <Box sx={{ mb: 3 }}>
          {dialogState.field && (
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <strong>Field:</strong> {dialogState.field}
            </Typography>
          )}
          {dialogState.error && (
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <strong>Error:</strong> {dialogState.error}
            </Typography>
          )}
          {dialogState.errorDetail && (
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <strong>Error Detail:</strong> {dialogState.errorDetail}
            </Typography>
          )}
        </Box>

        <FormControlStyled fullWidth error={showValidationError}>
          <FormLabelStyled id="dismissal-reason-label">
            Dismissal Reason{!dialogState.currentDismissalReason && ' *'}
          </FormLabelStyled>
          <Select
            labelId="dismissal-reason-label"
            id="dismissal-reason-select"
            value={selectedReason || ''}
            onChange={handleReasonChange}
            data-cy="select-dismissal-reason"
            variant="outlined"
            size="small"
            error={showValidationError}
          >
            {dialogState.currentDismissalReason && (
              <MenuItem value="">
                <em>Clear Dismissal (Re-enable Error)</em>
              </MenuItem>
            )}
            <MenuItem value={ValidationDismissalReason.NOT_PRINTED}>
              {ValidationDismissalReason.NOT_PRINTED}
            </MenuItem>
            <MenuItem value={ValidationDismissalReason.NOT_FOUND}>
              {ValidationDismissalReason.NOT_FOUND}
            </MenuItem>
            <MenuItem value={ValidationDismissalReason.NOT_READABLE}>
              {ValidationDismissalReason.NOT_READABLE}
            </MenuItem>
            <MenuItem value={ValidationDismissalReason.INCORRECT_VALIDATION}>
              {ValidationDismissalReason.INCORRECT_VALIDATION}
            </MenuItem>
          </Select>
          {showValidationError && (
            <FormHelperText error>Please select a dismissal reason</FormHelperText>
          )}
        </FormControlStyled>

        {!dialogState.isSingleExtractionMode && (
          <FormControlLabel
            control={
              <Checkbox
                checked={applyToAll}
                onChange={(e) => setApplyToAll(e.target.checked)}
                data-cy="checkbox-apply-to-all"
              />
            }
            label="Apply to all extractions"
            sx={{ mt: 2 }}
          />
        )}
      </DialogContentGlynt>
      <DialogActionsGlynt
        handleSubmit={handleSubmit}
        closeModal={handleClose}
        cancelCypressId="btn-dismissal-dialog-cancel"
        submitCypressId="btn-dismissal-dialog-submit"
        clearCypressId="btn-dismissal-dialog-clear"
        submitLoading={isSubmitting}
        submitDisabled={isSubmitting}
      />
    </DialogGlynt>
  );
};

export default ValidationDismissalDialog;
